import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import type { OrgRole } from '@/lib/roles';
import { activeOrgAtom, meAtom, type AuthMeResponse } from '@/stores/auth.atom';

/**
 * Named permissions, mirrored from the API catalog
 * (`apps/urbania360/api/src/common/auth/permissions.ts`). Keep both in sync.
 * The panel decides ONLY from the effective list the API sends in
 * `GET /auth/me`; the role is for display and structural rules.
 */
export const PERMISSIONS = [
  // Fixed
  'tours.view',
  'members.manage',
  'org.settings',
  // Configurable
  'tours.create',
  'tours.edit',
  'tours.publish',
  'tours.delete',
  'panoramas.edit',
  'panoramas.delete',
  'markers.edit',
  'folders.edit',
  'folders.delete',
  'links.manage',
  'collections.manage',
  'collections.delete',
  'integrations.manage',
  'stats.view',
  'members.view',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const EDITOR_DENIED: readonly Permission[] = ['tours.delete', 'panoramas.delete', 'folders.delete', 'collections.delete'];
const LOWER_FIXED: readonly Permission[] = ['tours.view'];

/**
 * Role defaults, used only when the API predates `permissions` (deploy skew)
 * so nothing disappears while the two halves roll out.
 */
const ROLE_DEFAULTS: Record<OrgRole, readonly Permission[]> = {
  owner: PERMISSIONS,
  admin: PERMISSIONS,
  editor: [
    ...LOWER_FIXED,
    ...PERMISSIONS.filter(p => !LOWER_FIXED.includes(p) && p !== 'members.manage' && p !== 'org.settings' && !EDITOR_DENIED.includes(p)),
  ],
  viewer: [...LOWER_FIXED, 'stats.view', 'members.view'],
};

type OrgWithPermissions = {
  role: string;
  permissions?: readonly string[];
};

/** Whether the member holds `permission` in `org`. Falls back to the role defaults when the API sent no list. */
export function can(org: OrgWithPermissions | null | undefined, permission: Permission): boolean {
  if (!org) return false;
  if (org.permissions) return org.permissions.includes(permission);
  return (ROLE_DEFAULTS[org.role as OrgRole] ?? []).includes(permission);
}

type MeOrg = AuthMeResponse['organizations'][number];

/** The organization of the current URL (`:orgSlug`), else the active one. */
export function useCurrentOrg(): MeOrg | null {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const me = useAtomValue(meAtom);
  const active = useAtomValue(activeOrgAtom);
  if (orgSlug) return me?.organizations.find(o => o.slug === orgSlug) ?? null;
  return active;
}

export function useCan(permission: Permission): boolean {
  return can(useCurrentOrg(), permission);
}

/** Same as `useCan` for an organization known by id (components that receive `orgId`). */
export function useCanInOrg(orgId: string | null | undefined, permission: Permission): boolean {
  const me = useAtomValue(meAtom);
  return can(me?.organizations.find(o => o.id === orgId), permission);
}

/** The panorama editor only serves members who can change markers or panoramas; the rest get the visitor preview. */
export function useCanOpenEditor(): boolean {
  const org = useCurrentOrg();
  return can(org, 'markers.edit') || can(org, 'panoramas.edit');
}
